import { useMemo, useState } from 'react'
import { useRouterState } from '@tanstack/react-router'
import {
  useAllEdges,
  useConcepts,
  useCourses,
  useExercises,
  useLessons,
  useModules,
  useSessions,
} from '@/db/hooks'
import { useDeck } from '@/state/deck'
import { nextUp } from '@/lib/learnDeck'
import { buildTest, type TestSetup } from '@/lib/learnTest'
import type { ExerciseCard } from '@/lib/learnDeck'
import { startSession } from '@/lib/learnSession'
import { AppTabs } from '@/components/AppTabs'
import { Button } from '@/components/ui'
import { seedDesignPatterns, teachingExerciseIds } from '@/lib/learnSeed'
import { useScope } from '@/state/scope'
import { ALL, DEFAULT_WORKSPACE_SLUG } from '@/kinds/workspace/schema'
import { useUi } from '@/state/ui'
import { FAMILIARITY_WORD } from '@/lib/learn'
import type { Familiarity } from '@/kinds/learn/schema'

/** Home and the Library: a switch at the top of the app, each its own route. */
export const LearnTabs = () => {
  const path = useRouterState({ select: (s) => s.location.pathname })
  return (
    <AppTabs
      label="Learn"
      tabs={[
        {
          to: '/learn',
          label: 'Home',
          on: path === '/learn' || /^\/learn\/(course|progress|settings)/.test(path),
        },
        {
          to: '/learn/library',
          label: 'Library',
          on: path.startsWith('/learn/library') || path.startsWith('/learn/concept'),
        },
      ]}
    />
  )
}

/** The suite a seed goes to: the one the device is in, the default one in Everything. */
export const useSeedSuite = () => {
  const scope = useScope((s) => s.ws)
  const home = useScope((s) => s.home)
  return scope === ALL ? home || DEFAULT_WORKSPACE_SLUG : scope
}

/** One tap adds the design patterns course. Adding it twice changes nothing. */
export const AddCourseButton = () => {
  const ws = useSeedSuite()
  const [busy, setBusy] = useState(false)
  return (
    <Button
      variant="primary"
      big
      disabled={busy}
      onClick={() => {
        setBusy(true)
        void seedDesignPatterns(ws).finally(() => setBusy(false))
      }}
      data-testid="learn-add-course"
    >
      Add the design patterns course
    </Button>
  )
}

/** Familiarity, said as a word: the only learning state there is. */
export const FamiliarityWord = ({ state }: { state: Familiarity }) => (
  <span className="text-xs text-faint shrink-0" data-testid="familiarity">
    {FAMILIARITY_WORD[state]}
  </span>
)

/** Opens the Add sheet on its Concept target: the plus is Prime's in a dev suite. */
export const AddConceptButton = () => {
  const open = useUi((s) => s.openConceptAdd)
  return (
    <Button big onClick={open} data-testid="learn-add-concept">
      Add a concept
    </Button>
  )
}

/**
 * Builds a deck from what the device holds and opens it. `start` returns false (and writes nothing) when
 * nothing is lined up, so the caller can say so quietly; `ready` is false until the records have been
 * read once (a push that opens the app cold waits for it).
 */
export const useStartDeck = () => {
  const courses = useCourses()
  const modules = useModules()
  const lessons = useLessons()
  const concepts = useConcepts()
  const exercises = useExercises()
  const sessions = useSessions()
  const edges = useAllEdges()
  const play = useDeck((s) => s.play)
  const ready = !!(courses && modules && lessons && concepts && exercises && sessions)
  const start = async (minutes: number): Promise<boolean> => {
    const now = new Date()
    const deck = nextUp(
      {
        courses: courses ?? [],
        modules: modules ?? [],
        lessons: lessons ?? [],
        concepts: concepts ?? [],
        exercises: exercises ?? [],
        sessions: sessions ?? [],
        edges,
      },
      minutes,
      now,
    )
    if (!deck.cards.length) return false
    play(deck, await startSession(deck, minutes, now), minutes)
    return true
  }
  return Object.assign(start, { ready })
}

/**
 * Whether the suite's course lacks cards the seed would make: none at all (a course from before the
 * teaching), or questions added since it was seeded. Not while the exercises are still being read.
 */
export const useTeachingGap = (): 'none' | 'some' | false => {
  const ws = useSeedSuite()
  const exercises = useExercises()
  const ids = useMemo(() => teachingExerciseIds(ws), [ws])
  if (!exercises) return false
  const have = new Set(exercises.filter((x) => !x.deleted_at).map((x) => x.id))
  if (!ids.some((id) => have.has(id))) return have.size === 0 ? 'none' : false
  return ids.some((id) => !have.has(id)) ? 'some' : false
}

/** One tap adds the teaching cards, or the questions added since (the seed again: what is there is left alone). */
export const AddTeachingButton = ({ gap = 'none' }: { gap?: 'none' | 'some' }) => {
  const ws = useSeedSuite()
  const [busy, setBusy] = useState(false)
  return (
    <Button
      big
      disabled={busy}
      onClick={() => {
        setBusy(true)
        void seedDesignPatterns(ws).finally(() => setBusy(false))
      }}
      data-testid="learn-add-teaching"
    >
      {gap === 'some' ? 'Add the new questions' : 'Add the teaching cards'}
    </Button>
  )
}

/**
 * Builds a test from what the device holds and opens it. false (and nothing written) when the scope has
 * no questions. `missed` is the second go at those questions.
 */
export const useStartTest = () => {
  const concepts = useConcepts()
  const exercises = useExercises()
  const edges = useAllEdges()
  const play = useDeck((s) => s.play)
  return async (setup: TestSetup, missed?: ExerciseCard[]): Promise<boolean> => {
    const deck = buildTest({ concepts: concepts ?? [], exercises: exercises ?? [], edges }, setup, missed)
    if (!deck) return false
    const minutes = setup.length ? Math.ceil(setup.length / 2) : 10
    play(deck, await startSession(deck, minutes), minutes)
    return true
  }
}
